import { useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { LoadingState, Notice, WorkspaceList, traceButtonClass } from '@traceio-org/auth-ui';
import { PRODUCTS } from '@traceio-org/platform-contracts';
import { useMeAccess } from '../app/access';
import { useHub } from '../app/context';
import { isSessionEnded, readHubState, useDecideAndFollow } from '../app/hooks';
import { AccountActions, DecideFailedNotice, HubFrame, PageHeading } from '../app/ui';
import { settingsHeaderActions } from '../settings/entry';

/**
 * /select-organization: the workspaces me/access returns, with their branding. Choosing one asks
 * platform-api to decide with that context, the requested product and the return path.
 */
export function SelectOrganizationPage() {
  const { config } = useHub();
  const location = useLocation();
  const { productKey, returnPath } = useMemo(() => readHubState(location.state), [location.state]);
  const [access, retry] = useMeAccess(productKey);
  const { run: runDecide, failed: decideFailed } = useDecideAndFollow();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [lastChoice, setLastChoice] = useState<string | null>(null);

  async function choose(contextId: string) {
    setBusyId(contextId);
    setLastChoice(contextId);
    const plan = await runDecide({
      contextId,
      ...(productKey ? { productKey } : {}),
      ...(returnPath ? { returnPath } : {}),
    });
    if (!plan || plan.type === 'navigate') setBusyId(null);
  }

  const lead =
    access.status === 'ready' && access.data.subject.email ? `Signed in as ${access.data.subject.email}.` : undefined;

  if (access.status === 'ready' && access.data.contexts.length === 0) return <Navigate to="/no-workspace" replace />;

  // With a requested product, show the workspaces that include it; if none does, show them all.
  const contexts =
    access.status === 'ready'
      ? (() => {
          const all = access.data.contexts;
          if (!productKey) return all;
          const withProduct = all.filter((c) => c.products.some((p) => p.productKey === productKey));
          return withProduct.length > 0 ? withProduct : all;
        })()
      : [];

  return (
    <HubFrame
      productKey={productKey ?? null}
      headerActions={settingsHeaderActions(access.status === 'ready' ? access.data.contexts : null)}
    >
      <PageHeading title="Choose a workspace" lead={lead} />
      {productKey ? <p className="trace-auth-muted">Workspaces for {PRODUCTS[productKey].displayName}</p> : null}
      {decideFailed && lastChoice && busyId === null ? <DecideFailedNotice onRetry={() => void choose(lastChoice)} /> : null}
      {access.status === 'loading' ? <LoadingState label="Loading your workspaces" /> : null}
      {access.status === 'error' && !isSessionEnded(access.error) ? (
        <Notice tone="error" live="assertive" title="We could not load your workspaces.">
          <button type="button" className={traceButtonClass('primary', { block: true })} onClick={retry}>
            Try again
          </button>
        </Notice>
      ) : null}
      {access.status === 'ready' ? (
        <WorkspaceList contexts={contexts} onSelect={choose} busyId={busyId} assetBaseUrl={config.platformApiUrl} />
      ) : null}
      <AccountActions productKey={productKey} />
    </HubFrame>
  );
}
